import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ProductInformation } from 'features/menu/components/product-detail/product-information';
import { ProductNutritionInformation } from 'features/menu/components/product-detail/product-nutrition-information/product-nutrition-information';
import { CurvedBackground } from 'features/menu/components/product-detail/product-overview/curved-bg';
import {
  ProductImage,
  ProductOverviewContainer,
} from 'features/menu/components/product-detail/product-overview/styled';
import { Animated, LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CatalogComboSlotsSection } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/catalog-combo-slots-section';
import { CatalogIncludedSection } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/catalog-included-section';
import {
  buildCatalogIndexedMenu,
  getCatalogSelectedNutritionItems,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/get-catalog-included';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { OneMenuCatalogEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { IIngredients, IndexedMenu } from 'state/unified-menu/types';
import { IUnifiedMenuContext, UnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { isCombo, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { primitive, spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { hapticImpact } from 'utils/haptic';

type MenuCatalogPreviewProps = {
  entry: OneMenuCatalogEntry;
  entries?: readonly OneMenuCatalogEntry[];
  ingredients?: IIngredients[];
  onSelectRestaurant: () => void;
};

/** No-store menu deeplink preview: product + included + info sections, then store CTA. */
export function MenuCatalogPreview({
  entry,
  entries = [],
  ingredients = [],
  onSelectRestaurant,
}: MenuCatalogPreviewProps) {
  const { language } = useLocale();
  const selectedProduct = asPreviewMenuEntry(entry, language);
  const name = selectedProduct.name?.locale || '';
  const imageResource = selectedProduct.image?.resource;
  const imageAlt = selectedProduct.image?.altText?.locale || name;
  const image = imageResource ? mapImageResourceToSanityImage(imageResource, imageAlt) : undefined;
  const [bgHeight, setBgHeight] = useState(0);
  const [ctaHighlighted, setCtaHighlighted] = useState(false);
  const ctaScale = useRef(new Animated.Value(1)).current;
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const catalogMenu = useMemo(
    () => buildCatalogIndexedMenu(entries.length ? entries : [entry], language),
    [entries, entry, language]
  );

  const selectedNutritionItems = useMemo(
    () => getCatalogSelectedNutritionItems(catalogMenu, selectedProduct),
    [catalogMenu, selectedProduct]
  );

  const highlightSelectRestaurant = useCallback(() => {
    void hapticImpact();
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setCtaHighlighted(true);
    ctaScale.setValue(1);
    Animated.sequence([
      Animated.timing(ctaScale, { toValue: 1.04, duration: 120, useNativeDriver: true }),
      Animated.timing(ctaScale, { toValue: 1, duration: 120, useNativeDriver: true }),
      Animated.timing(ctaScale, { toValue: 1.04, duration: 120, useNativeDriver: true }),
      Animated.timing(ctaScale, { toValue: 1, duration: 120, useNativeDriver: true }),
    ]).start();
    highlightTimeoutRef.current = setTimeout(() => {
      setCtaHighlighted(false);
      highlightTimeoutRef.current = null;
    }, 1200);
  }, [ctaScale]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const unifiedMenuValue = useMemo<IUnifiedMenuContext>(
    () => ({
      activeDayParts: [],
      dayParts: [],
      rawMenu: undefined,
      indexedMenu: catalogMenu,
      indexedRewards: IndexedMenu.EMPTY,
      indexedOffers: IndexedMenu.EMPTY,
      loadedStoreNumber: null,
      mainMenu: [],
      // Preview has no store day-part source; treat as resolved with empty parts.
      isDayPartsSourceResolved: true,
      isLoading: false,
      hasError: false,
      refetchMenu: async () => undefined,
      ingredients,
    }),
    [catalogMenu, ingredients]
  );

  return (
    <UnifiedMenuContext.Provider value={unifiedMenuValue}>
      <CardModal
        alwaysShowHeader
        headerText={name}
        scrollView
        // Match PDP ProductHome: white below the mayo curve so it meets Included.
        scrollContainerStyle={styles.scrollContent}
        wrappedChildrenStyle={styles.whiteSurface}
        footer={
          <View style={styles.footer}>
            <Animated.View style={{ transform: [{ scale: ctaScale }], width: '100%' }}>
              <ActionButton
                testID="menu-catalog-preview-select-restaurant"
                fullWidth
                // Pulse to PRIMARY briefly when a combo slot is tapped.
                variant={
                  ctaHighlighted ? ActionButtonVariants.PRIMARY : ActionButtonVariants.OUTLINE
                }
                onPress={onSelectRestaurant}
                leftIcon={
                  <Icon name="mi:location-on" width="24px" {...hiddenAccessibilityPlatformProps} />
                }
              >
                {t('selectRestaurantToContinue')}
              </ActionButton>
            </Animated.View>
          </View>
        }
      >
        <View style={styles.content} testID="menu-catalog-preview">
          {image ? (
            <ProductOverviewContainer withSpacing={false}>
              <View style={[styles.curvedBackground, { height: bgHeight }]}>
                <CurvedBackground
                  height={bgHeight}
                  curveColor={primitive.$white}
                  backgroundColor={primitive.$mayoEggWhite}
                />
              </View>
              <ProductImage
                image={image}
                alt={imageAlt}
                onLayout={({ nativeEvent }: LayoutChangeEvent) => {
                  const { height } = nativeEvent.layout;
                  setBgHeight(height - height * 0.4);
                }}
              />
            </ProductOverviewContainer>
          ) : null}

          <CatalogComboSlotsSection
            menu={catalogMenu}
            product={selectedProduct}
            onRequireRestaurantSelection={highlightSelectRestaurant}
          />

          {/* Combos list slot products above; Included modifiers are item/picker only. */}
          {!isCombo(selectedProduct) ? (
            <CatalogIncludedSection menu={catalogMenu} product={selectedProduct} />
          ) : null}

          <View style={styles.info}>
            {selectedProduct.description?.locale ? (
              <ProductInformation selectedProduct={selectedProduct} />
            ) : null}

            <ProductNutritionInformation
              selectedProduct={selectedProduct}
              nutritionItems={selectedNutritionItems}
            />
          </View>
        </View>
      </CardModal>
    </UnifiedMenuContext.Provider>
  );
}

const styles = StyleSheet.create({
  content: {
    backgroundColor: primitive.$white,
    flexGrow: 1,
    width: '100%',
  },
  curvedBackground: {
    position: 'absolute',
    width: '100%',
  },
  footer: {
    paddingHorizontal: spacing.$4,
    width: '100%',
  },
  info: {
    backgroundColor: primitive.$mayoEggWhite,
    // Fill any space left above the footer so the preview does not end in a
    // white gap on short products.
    flexGrow: 1,
    paddingHorizontal: spacing.$4,
    width: '100%',
  },
  scrollContent: {
    backgroundColor: primitive.$white,
    flexGrow: 1,
  },
  whiteSurface: {
    backgroundColor: primitive.$white,
    flexGrow: 1,
  },
});
